/* eslint-disable complexity */
/* eslint-disable react-hooks/exhaustive-deps */
import type {
  ButtonShape,
  DropdownPlacement,
  OptionType,
  Options
} from '@eggplant/types'
import type {
  Overwrite,
  UseSelectGetItemPropsOptions,
  UseSelectGetItemPropsReturnValue
} from 'downshift'
import { useSelect } from 'downshift'
import natsort from 'natsort'
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import { FiX } from 'react-icons/fi'
import { HiCheck } from 'react-icons/hi'
import {
  DropdownButton,
  InvisiButton
} from '../..'
import classes from '../Combobox/Combobox.module.scss'

interface ButtonProps {
  Icon?: ({ size }: {size: string}) => ReactNode
  prompt?: string
  customPrompt?: ReactNode
  customTooltip?: string
  categoryLabel?: string
  isHighlighted: boolean
  isDisabled?: boolean
  isInvalid?: boolean
}

interface Props {
  customValidity?: string
  disableSorting?: boolean
  options?: Options
  initialOptions?: Options
  isRequired?: boolean
  onChange: (value: string) => void
  onClear?: () => void
  value?: string
  noOptionsPlaceholder?: ReactNode
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  ariaLabel?: string
  isClearable?: boolean
  dropdownPlacement?: DropdownPlacement
}

const itemToString = (option?: OptionType | null) => option
  ? option.label
  : ''

const ComboboxSingleSelect = ({
  ariaLabel,
  customValidity = 'Please select an item in the list.',
  disableSorting = false,
  Icon,
  isHighlighted,
  options = [],
  onChange,
  onClear,
  initialOptions,
  isRequired = false,
  value = '',
  isDisabled = false,
  isInvalid = false,
  noOptionsPlaceholder,
  prompt = 'Please select an option',
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  isClearable = true,
  dropdownPlacement = 'bottom',
  customPrompt,
  customTooltip,
  categoryLabel
}: (Props & ButtonProps)) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const comboBoxRef = useRef<HTMLSpanElement>(null)

  const sorter = natsort({ insensitive: true })

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedOptions = options.slice().sort((current, next) =>
    sorter(current.label, next.label)
  )

  const handleClear = () => {
    if (onClear) {
      onClear()
    }

    onChange('')
  }

  const renderClearButton = (
    <div
      aria-label='dropdown-footer'
      className={classes.clearSection}
    >
      <InvisiButton
        hasHoverEffect
        label='Clear'
        onClick={handleClear}
      >
        <div className={classes.clearWrapper}>
          <FiX
            aria-hidden
            size={16}
          />
          Clear
        </div>
      </InvisiButton>
    </div>
  )

  const isSelectedClass = (item: OptionType) => item.value === value
    ? ' ' + classes.isSelected
    : ''

  const renderOptionLabel = (item: OptionType) =>
    <div className={classes.checkboxStyle}>
      <div className={classes.textStyle}>
        {item.trailingIcon && item.trailingIcon}
        {item.label}
      </div>
      {
        item.value === value &&
        <HiCheck
          aria-hidden
          aria-label='Combobox selected checked'
          className={classes.CheckIcon}
          size={16}
        />
      }
    </div>

  const renderOptions = (getItemProps: <Options>(options: UseSelectGetItemPropsOptions<OptionType> & Options,) => Omit<Overwrite<UseSelectGetItemPropsReturnValue, Options>, 'index' | 'item'>, highlightedIndex: number) => {
    const optionsList = disableSorting
      ? options
      : sortedOptions

    const combinedOptions = initialOptions
      ? [...initialOptions, ...optionsList]
      : optionsList

    const itemDisableClassStyle = disableSorting
      ? classes.dividerStyle
      : ''

    return combinedOptions.length
      ? combinedOptions.map((item, index) =>
        <li
          key={item.value}
          {...getItemProps({
            item,
            index
          })}
          className={
            `${isSelectedClass(item)} 
            ${highlightedIndex === index && classes.highlighted} 
            ${index === 0 && itemDisableClassStyle}`
          }
          title={item.label}
        >
          {renderOptionLabel(item)}
        </li>
      )
      : null
  }

  const inputRef = useRef<HTMLInputElement>(null)

  const getComboboxStyle = () => {
    const enabledStyle = !isDisabled && !isInvalid
      ? classes.Enabled
      : ''

    const hasControlledWidthStyle = isFilter && isFilterFixedMinMaxWidth
      ? classes.FilterComboboxMinMaxWidth
      : ''

    const comboBoxClasses = [
      classes.Combobox,
      enabledStyle,
      hasControlledWidthStyle
    ].filter(className => className)

    return comboBoxClasses.join(' ')
  }

  const {
    isOpen,
    selectedItem,
    selectItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect({
    items: disableSorting
      ? options
      : sortedOptions,
    itemToString
  })

  const getRenderButton = () => {
    const currentOption = options.find(item =>
      item.value === value
    )

    const category = categoryLabel
      ? `${categoryLabel}: `
      : ''

    const filterTooltip = isFilter
      ? category + currentOption?.label
      : currentOption?.label

    const defaultTitle = currentOption?.value
      ? filterTooltip
      : prompt

    const title = customTooltip || defaultTitle

    const label = currentOption
      ? currentOption.label
      : prompt

    const renderContent = customPrompt || label

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={Icon}
        dropdownPlacement={dropdownPlacement}
        isDisabled={isDisabled}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isFocus={isOpen}
        isHighlighted={value !== '' && isHighlighted}
        isInvalid={isInvalid}
        label={label}
        title={title}
      >
        {renderContent}
      </DropdownButton>
  }

  const validate = () => {
    const ref = inputRef.current

    if (!ref) return

    // reset CustomValidity when value changed
    ref.setCustomValidity('')

    if (value === selectedItem?.value) return

    if (value) {
      const selectedOption = options.find(option => option.value === value)

      if (selectedOption) selectItem(selectedOption)
    }
  }

  useEffect(validate, [value])

  useEffect(() => {
    if (value === selectedItem?.value) return

    if (selectedItem) onChange(selectedItem.value)
  }, [selectedItem])

  const dropdownWrapperClass = !isOpen
    ? classes.dropdownWrapper + ' ' + classes.Hidden
    : classes.dropdownWrapper

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  window.addEventListener('resize', handleResize)

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLSpanElement | null>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { left: '0px' }
    }

    const comboxBoxPos = comboBoxRef.current.getBoundingClientRect().left + 415
    const adjustedLeft = 400 - comboBoxRef.current.clientWidth // fixed dropdown width = 400

    if (comboxBoxPos > windowWidth) {
      return { left: `-${adjustedLeft}px` }
    }

    return { left: '0px' }
  }

  const getComboBoxRightPositionStyle = (comboBoxRef: React.RefObject<HTMLSpanElement | null>): CSSProperties => {
    if (!comboBoxRef.current) {
      return {
        top: '-2px',
        left: '115px'
      }
    }

    const comboxBoxPos = comboBoxRef.current.offsetWidth

    return {
      top: '-2px',
      left: `${comboxBoxPos + 5}px`
    }
  }

  return (
    <span
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      className={getComboboxStyle()}
      ref={comboBoxRef}
      role={undefined}
    >
      <input
        checked={!!value}
        onInvalid={(event: React.ChangeEvent<HTMLInputElement>) => event.target.setCustomValidity(customValidity)}
        readOnly
        ref={inputRef}
        required={isRequired}
        type='checkbox'
      />
      {getRenderButton()(getToggleButtonProps())}
      <span
        className={dropdownWrapperClass}
        style={
          dropdownPlacement === 'bottom'
            ? getComboBoxLeftPositionStyle(comboBoxRef)
            : getComboBoxRightPositionStyle(comboBoxRef)
        }
      >
        {
          options.length
            ? (
              <>
                <ul {...getMenuProps({}, { suppressRefError: true })}>
                  {
                    isOpen && !isDisabled &&
                          !!options.length && renderOptions(getItemProps, highlightedIndex)
                  }
                </ul>
                {(value && isClearable) && renderClearButton}
              </>
            )
            : noOptionsPlaceholder
        }
      </span>
    </span>
  )
}

export default ComboboxSingleSelect
